/**
 * SkillAssessment — Sprint 2 / TASK 3-7
 *
 * Geektrust-style 5-question MCQ skill assessment with:
 *   • Skill chip selector seeded from user_profiles.skills (free-text fallback).
 *   • Sequential one-question-per-screen UI (similar to onboarding).
 *   • Edge-function-backed quiz generation + grading.
 *   • Best-effort profile-vector recalibration on completion.
 *
 * Flow states:
 *   ┌──────────┐   pick   ┌────────┐   start  ┌──────┐  next/prev  ┌──────┐
 *   │  select  ├─────────►│ ready  ├─────────►│ quiz ├────────────►│ quiz │
 *   └──────────┘          └────────┘          └──┬───┘             └──┬───┘
 *                                                │ submit             │ submit
 *                                                ▼                    ▼
 *                                            ┌────────┐
 *                                            │ result │  ◄── recalibration toast
 *                                            └────────┘
 *
 * Hard rules (TASK 3-7):
 *   • DO NOT cache quiz questions across users — every quiz is fresh.
 *   • Each MCQ shows the correct answer + explanation post-submit.
 *   • Profile vector recalibration is best-effort; UI must not fail if Pinecone is down.
 */

import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  Loader2,
  RefreshCw,
  Sparkles,
  Trophy,
  X,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../../lib/supabase';
import { useUserProfile } from '../hooks/useUserProfile';

// ── Types ─────────────────────────────────────────────────────────────────────

type SkillLevel = 'entry' | 'mid' | 'senior' | 'lead';

interface QuizQuestion {
  q: string;
  options: string[];
  /** Server-only — only present in the result payload after submission. */
  correct_index?: number;
  explanation?: string;
}

interface GenerateQuizResponse {
  success: boolean;
  assessment_id?: string;
  skill?: string;
  level?: SkillLevel;
  questions?: QuizQuestion[];
  error?: string;
}

interface ScoredQuestion {
  q: string;
  options: string[];
  correct_index: number;
  explanation: string;
  selected_index: number;
  is_correct: boolean;
}

interface ScoreQuizResponse {
  success: boolean;
  assessment_id?: string;
  skill?: string;
  level?: SkillLevel;
  score?: number;
  correct?: number;
  total?: number;
  feedback?: string;
  questions?: ScoredQuestion[];
  completed_at?: string;
  error?: string;
}

type Phase = 'select' | 'loading_quiz' | 'quiz' | 'submitting' | 'result' | 'error';

interface ResultState {
  score:    number;
  correct:  number;
  total:    number;
  feedback: string;
  graded:   ScoredQuestion[];
}

// ── Component props ───────────────────────────────────────────────────────────

export interface SkillAssessmentProps {
  /** Pre-select a skill (e.g. when launched from a Job's missing-skills CTA). */
  initialSkill?: string;
  /** Pre-select a level. Defaults to 'mid'. */
  initialLevel?: SkillLevel;
  /** Optional close handler — when provided, renders a back button. */
  onClose?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const SkillAssessment: React.FC<SkillAssessmentProps> = ({
  initialSkill,
  initialLevel = 'mid',
  onClose,
}) => {
  const { t } = useTranslation();
  const { profile } = useUserProfile();

  const levels = useMemo(() => [
    { id: 'entry' as SkillLevel, label: t('dashboard.skillAssessment.levelEntry', 'Entry'), description: t('dashboard.skillAssessment.levelEntryYears', '0–2 years') },
    { id: 'mid' as SkillLevel, label: t('dashboard.skillAssessment.levelMid', 'Mid'), description: t('dashboard.skillAssessment.levelMidYears', '2–5 years') },
    { id: 'senior' as SkillLevel, label: t('dashboard.skillAssessment.levelSenior', 'Senior'), description: t('dashboard.skillAssessment.levelSeniorYears', '5–10 years') },
    { id: 'lead' as SkillLevel, label: t('dashboard.skillAssessment.levelLead', 'Lead'), description: t('dashboard.skillAssessment.levelLeadYears', '10+ years') },
  ], [t]);

  // Skill chip options — profile skills + popular fallbacks if profile is empty.
  const skillChips: string[] = useMemo(() => {
    const profileSkills = (profile?.skills ?? []).filter(
      (s) => typeof s === 'string' && s.trim().length > 0,
    );
    if (profileSkills.length > 0) return profileSkills.slice(0, 18);
    return [
      'JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'SQL',
      'AWS', 'Docker', 'System Design', 'Communication',
    ];
  }, [profile?.skills]);

  // ── Selection state (Phase: select) ────────────────────────────────────────
  const [selectedSkill, setSelectedSkill] = useState<string>(initialSkill?.trim() ?? '');
  const [customSkill,   setCustomSkill]   = useState<string>('');
  const [level,         setLevel]         = useState<SkillLevel>(initialLevel);

  // ── Quiz state (Phase: quiz) ───────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('select');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [currentIdx, setCurrentIdx] = useState<number>(0);

  // ── Result state ───────────────────────────────────────────────────────────
  const [result, setResult] = useState<ResultState | null>(null);
  const [recalibrating, setRecalibrating] = useState<boolean>(false);
  const [recalibrated, setRecalibrated] = useState<boolean>(false);

  // ── Reset shortcut (Try another skill) ─────────────────────────────────────
  const reset = useCallback(() => {
    setPhase('select');
    setErrorMsg(null);
    setAssessmentId(null);
    setQuestions([]);
    setAnswers({});
    setCurrentIdx(0);
    setResult(null);
    setRecalibrated(false);
    setRecalibrating(false);
  }, []);

  // ── Step 1: Generate quiz ──────────────────────────────────────────────────
  const startQuiz = useCallback(async () => {
    const skill = (selectedSkill || customSkill).trim();
    if (!skill) {
      setErrorMsg(t('dashboard.skillAssessment.pickSkill', 'Pick a skill to start the quiz.'));
      return;
    }

    setPhase('loading_quiz');
    setErrorMsg(null);

    const { data, error } = await supabase.functions.invoke<GenerateQuizResponse>(
      'generate-skill-quiz',
      { body: { skill, level } },
    );

    if (error || !data?.success || !data.assessment_id || !data.questions) {
      setPhase('error');
      setErrorMsg(
        data?.error === 'llm_unavailable' || data?.error === 'groq_unavailable'
          ? t('dashboard.skillAssessment.quizBusy', 'The quiz service is busy right now. Please try again in a moment.')
          : t('dashboard.skillAssessment.quizGenFailed', 'Could not generate a quiz for this skill. Try a different skill or level.'),
      );
      return;
    }

    setAssessmentId(data.assessment_id);
    setQuestions(data.questions);
    setAnswers({});
    setCurrentIdx(0);
    setPhase('quiz');
  }, [selectedSkill, customSkill, level, t]);

  // ── Step 2: Submit answers ─────────────────────────────────────────────────
  const submitQuiz = useCallback(async () => {
    if (!assessmentId) return;
    if (Object.keys(answers).length !== questions.length) {
      setErrorMsg(t('dashboard.skillAssessment.answerAll', 'Answer every question before submitting.'));
      return;
    }

    setPhase('submitting');
    setErrorMsg(null);

    const ordered: number[] = [];
    for (let i = 0; i < questions.length; i++) {
      ordered.push(answers[i] ?? -1);
    }

    const { data, error } = await supabase.functions.invoke<ScoreQuizResponse>(
      'score-skill-assessment',
      { body: { assessment_id: assessmentId, answers: ordered } },
    );

    if (error || !data?.success || data.score == null || !Array.isArray(data.questions)) {
      setPhase('quiz'); // let the user retry submission
      setErrorMsg(t('dashboard.skillAssessment.scoreFailed', 'Could not score the quiz. Please try submitting again.'));
      return;
    }

    setResult({
      score:    data.score,
      correct:  data.correct ?? 0,
      total:    data.total ?? questions.length,
      feedback: data.feedback ?? '',
      graded:   data.questions,
    });
    setPhase('result');

    // Best-effort recalibration. Never blocks the UI; we surface a toast on success.
    void (async () => {
      setRecalibrating(true);
      try {
        const { error: vecErr } = await supabase.functions.invoke('upsert-profile-vector', {
          body: {},
        });
        if (!vecErr) setRecalibrated(true);
      } catch {
        // swallow — Pinecone outage is non-blocking per TASK 3-7 rule.
      } finally {
        setRecalibrating(false);
      }
    })();
  }, [assessmentId, answers, questions.length, t]);

  // ── Convenience: navigation handlers for the quiz ──────────────────────────
  const goNext = useCallback(() => {
    if (currentIdx < questions.length - 1) setCurrentIdx((i) => i + 1);
  }, [currentIdx, questions.length]);

  const goPrev = useCallback(() => {
    if (currentIdx > 0) setCurrentIdx((i) => i - 1);
  }, [currentIdx]);

  const pickAnswer = useCallback((qIdx: number, optionIdx: number) => {
    setAnswers((prev) => ({ ...prev, [qIdx]: optionIdx }));
    setErrorMsg(null);
  }, []);

  // Auto-recalibrated toast dismiss (3 s).
  useEffect(() => {
    if (!recalibrated) return;
    const t = setTimeout(() => setRecalibrated(false), 3_000);
    return () => clearTimeout(t);
  }, [recalibrated]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div data-tour="page-skill-assessment" className="flex-1 min-h-0 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        {/* Header */}
        <header className="space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--vscode-primary)]" />
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight ggj-page-title">
              {t('dashboard.skillAssessment.title', 'Skill assessment')}
            </h1>
          </div>
          <p className="text-sm vscode-text-secondary">
            {t('dashboard.skillAssessment.subtitle', "5 questions, ~3 minutes. We'll use the result to refine your job recommendations.")}
          </p>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-sm vscode-text-tertiary hover:vscode-text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> {t('dashboard.skillAssessment.back', 'Back')}
            </button>
          )}
        </header>

        {/* ── Phase: SELECT ─────────────────────────────────────────────── */}
        {phase === 'select' && (
          <section className="space-y-6 rounded-xl border vscode-border vscode-bg-secondary p-4 sm:p-6">
            <div>
              <h2 className="text-lg font-semibold vscode-text-primary mb-3">
                {t('dashboard.skillAssessment.pickSkillTitle', 'Pick a skill')}
              </h2>
              <div className="flex flex-wrap gap-2">
                {skillChips.map((skill) => {
                  const isActive = selectedSkill.toLowerCase() === skill.toLowerCase();
                  return (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => {
                        setSelectedSkill(skill);
                        setCustomSkill('');
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border transition-all
                        ${isActive
                          ? 'bg-[var(--vscode-primary)] text-white border-transparent shadow-sm'
                          : 'vscode-bg-tertiary vscode-border vscode-text-secondary hover:vscode-text-primary'}
                      `}
                    >
                      {isActive && <Check className="w-3 h-3" />}
                      {skill}
                    </button>
                  );
                })}
              </div>
              {(profile?.skills?.length ?? 0) === 0 && (
                <p className="mt-2 text-xs vscode-text-tertiary">
                  {t('dashboard.skillAssessment.addSkillsHint', 'Add skills to your profile to personalize this list.')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium vscode-text-primary mb-2">
                {t('dashboard.skillAssessment.customSkillLabel', 'Or type any skill')}
              </label>
              <input
                type="text"
                value={customSkill}
                onChange={(e) => {
                  setCustomSkill(e.target.value);
                  setSelectedSkill('');
                }}
                placeholder={t('dashboard.skillAssessment.customSkillPlaceholder', 'e.g. GraphQL, Kubernetes, FastAPI…')}
                maxLength={80}
                className="w-full px-3 h-11 rounded-xl border vscode-border vscode-bg-primary text-sm vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)]/40"
              />
            </div>

            <div>
              <label className="block text-sm font-medium vscode-text-primary mb-2">
                {t('dashboard.skillAssessment.difficulty', 'Difficulty')}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {levels.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setLevel(l.id)}
                    className={`min-h-[60px] px-3 py-2 rounded-xl border text-left transition-all
                      ${level === l.id
                        ? 'border-[var(--vscode-primary)] bg-[var(--vscode-primary)]/10'
                        : 'vscode-border vscode-bg-tertiary hover:vscode-bg-hover'}
                    `}
                  >
                    <div className={`text-sm font-semibold ${level === l.id ? 'text-[var(--vscode-primary)]' : 'vscode-text-primary'}`}>
                      {l.label}
                    </div>
                    <div className="text-[11px] vscode-text-tertiary mt-0.5">
                      {l.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {errorMsg && (
              <p role="alert" className="text-sm text-red-500">
                {errorMsg}
              </p>
            )}

            <button
              type="button"
              onClick={() => void startQuiz()}
              disabled={!(selectedSkill || customSkill.trim())}
              className="w-full sm:w-auto min-h-[44px] px-5 rounded-xl bg-[var(--vscode-primary)] disabled:bg-white/10 disabled:text-white/40 text-white text-sm font-semibold hover:opacity-90 transition-opacity touch-manipulation inline-flex items-center justify-center gap-2"
            >
              {t('dashboard.skillAssessment.startQuiz', 'Start quiz')} <ArrowRight className="w-4 h-4" />
            </button>
          </section>
        )}

        {/* ── Phase: LOADING ───────────────────────────────────────────── */}
        {phase === 'loading_quiz' && (
          <section className="rounded-xl border vscode-border vscode-bg-secondary p-8 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 vscode-text-primary animate-spin" />
            <p className="text-sm vscode-text-secondary">
              {t('dashboard.skillAssessment.generatingFor', {
                defaultValue: 'Generating 5 fresh questions for {{skill}}…',
                skill: selectedSkill || customSkill,
              })}
            </p>
            <p className="text-xs vscode-text-tertiary">{t('dashboard.skillAssessment.generatingHint', 'This usually takes 4–7 seconds.')}</p>
          </section>
        )}

        {/* ── Phase: QUIZ + SUBMITTING ─────────────────────────────────── */}
        {(phase === 'quiz' || phase === 'submitting') && questions.length > 0 && (
          <QuizPanel
            questions={questions}
            answers={answers}
            currentIdx={currentIdx}
            onPick={pickAnswer}
            onNext={goNext}
            onPrev={goPrev}
            onSubmit={() => void submitQuiz()}
            isSubmitting={phase === 'submitting'}
            errorMsg={errorMsg}
          />
        )}

        {/* ── Phase: RESULT ────────────────────────────────────────────── */}
        {phase === 'result' && result && (
          <ResultPanel
            skill={selectedSkill || customSkill}
            level={level}
            result={result}
            onRetry={reset}
            recalibrating={recalibrating}
            recalibrated={recalibrated}
          />
        )}

        {/* ── Phase: ERROR ─────────────────────────────────────────────── */}
        {phase === 'error' && (
          <section className="rounded-xl border border-red-500/40 bg-red-500/5 p-4 sm:p-6 space-y-3">
            <p className="text-sm text-red-600 dark:text-red-400">
              {errorMsg ?? t('dashboard.skillAssessment.genericError', 'Something went wrong while generating your quiz.')}
            </p>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 min-h-[40px] px-4 rounded-xl border vscode-border vscode-text-primary text-sm font-medium hover:vscode-bg-hover"
            >
              <RefreshCw className="w-4 h-4" /> {t('dashboard.skillAssessment.tryAgain', 'Try again')}
            </button>
          </section>
        )}
      </div>
    </div>
  );
};

// ── Sub-component: QuizPanel ─────────────────────────────────────────────────

interface QuizPanelProps {
  questions: QuizQuestion[];
  answers: Record<number, number>;
  currentIdx: number;
  onPick: (qIdx: number, optionIdx: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  errorMsg: string | null;
}

const QuizPanel: React.FC<QuizPanelProps> = ({
  questions,
  answers,
  currentIdx,
  onPick,
  onNext,
  onPrev,
  onSubmit,
  isSubmitting,
  errorMsg,
}) => {
  const { t } = useTranslation();
  const total = questions.length;
  const q = questions[currentIdx];
  const selected = answers[currentIdx];
  const allAnswered = Object.keys(answers).length === total;
  const isLast = currentIdx === total - 1;

  if (!q) return null;

  return (
    <section className="space-y-4">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <p className="text-xs vscode-text-tertiary uppercase tracking-wider font-semibold">
          {t('dashboard.skillAssessment.questionProgress', {
            defaultValue: 'Question {{current}} of {{total}}',
            current: currentIdx + 1,
            total,
          })}
        </p>
        <p className="text-xs vscode-text-tertiary">
          {t('dashboard.skillAssessment.answeredProgress', {
            defaultValue: '{{answered}} / {{total}} answered',
            answered: Object.keys(answers).length,
            total,
          })}
        </p>
      </div>
      <div className="h-1.5 rounded-full vscode-bg-tertiary overflow-hidden">
        <div
          className="h-full rounded-full bg-[var(--vscode-primary)] transition-all"
          style={{ width: `${((currentIdx + 1) / total) * 100}%` }}
        />
      </div>

      {/* Question */}
      <div className="rounded-xl border vscode-border vscode-bg-secondary p-4 sm:p-6 space-y-4">
        <h3 className="text-base sm:text-lg font-semibold vscode-text-primary leading-snug">
          {q.q}
        </h3>

        <div className="space-y-2">
          {q.options.map((opt, i) => {
            const isSelected = selected === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onPick(currentIdx, i)}
                disabled={isSubmitting}
                className={`w-full flex items-start gap-3 px-4 py-3 rounded-xl border text-left transition-all touch-manipulation
                  ${isSelected
                    ? 'border-[var(--vscode-primary)] bg-[var(--vscode-primary)]/10'
                    : 'vscode-border vscode-bg-primary hover:vscode-bg-hover'}
                  ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <div className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold
                  ${isSelected
                    ? 'border-[var(--vscode-primary)] bg-[var(--vscode-primary)] text-white'
                    : 'vscode-border vscode-text-tertiary'}
                `}>
                  {String.fromCharCode(65 + i)}
                </div>
                <span className="text-sm vscode-text-primary leading-relaxed pt-0.5">{opt}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Question dots — visual indicator only; navigation is via Prev/Next. */}
      <div
        className="flex items-center justify-center gap-1.5"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={currentIdx + 1}
        aria-label={t('dashboard.skillAssessment.quizProgressAria', 'Quiz progress')}
      >
        {questions.map((_, i) => {
          const isAnswered = answers[i] !== undefined;
          const isCurrent = i === currentIdx;
          return (
            <span
              key={i}
              aria-hidden
              className={`h-2 rounded-full transition-all
                ${isCurrent
                  ? 'bg-[var(--vscode-primary)] w-6'
                  : isAnswered
                    ? 'bg-[var(--vscode-primary)]/60 w-2'
                    : 'vscode-bg-tertiary w-2'}
              `}
            />
          );
        })}
      </div>

      {errorMsg && (
        <p role="alert" className="text-sm text-red-500 text-center">{errorMsg}</p>
      )}

      {/* Nav buttons */}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={currentIdx === 0 || isSubmitting}
          className="min-h-[44px] px-4 rounded-xl border vscode-border text-sm font-medium vscode-text-primary hover:vscode-bg-hover disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 touch-manipulation"
        >
          <ArrowLeft className="w-4 h-4" /> {t('dashboard.skillAssessment.previous', 'Previous')}
        </button>

        {isLast ? (
          <button
            type="button"
            onClick={onSubmit}
            disabled={!allAnswered || isSubmitting}
            className="min-h-[44px] px-5 rounded-xl bg-[var(--vscode-primary)] disabled:bg-white/10 disabled:text-white/40 text-white text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 transition-opacity touch-manipulation"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {t('dashboard.skillAssessment.scoring', 'Scoring…')}
              </>
            ) : (
              <>
                {t('dashboard.skillAssessment.submit', 'Submit')} <Check className="w-4 h-4" />
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={selected === undefined || isSubmitting}
            className="min-h-[44px] px-5 rounded-xl bg-[var(--vscode-primary)] disabled:bg-white/10 disabled:text-white/40 text-white text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 transition-opacity touch-manipulation"
          >
            {t('dashboard.skillAssessment.next', 'Next')} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </section>
  );
};

// ── Sub-component: ResultPanel ───────────────────────────────────────────────

interface ResultPanelProps {
  skill: string;
  level: SkillLevel;
  result: ResultState;
  onRetry: () => void;
  recalibrating: boolean;
  recalibrated: boolean;
}

const ResultPanel: React.FC<ResultPanelProps> = ({
  skill,
  level,
  result,
  onRetry,
  recalibrating,
  recalibrated,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<number, boolean>>(
    () => Object.fromEntries(result.graded.map((q) => [result.graded.indexOf(q), !q.is_correct])),
  );
  const toggle = (i: number) => setExpanded((e) => ({ ...e, [i]: !e[i] }));

  const scoreBand =
    result.score >= 80 ? { label: t('dashboard.skillAssessment.bandStrong', 'Strong'),     color: 'green'  } :
    result.score >= 60 ? { label: t('dashboard.skillAssessment.bandSolid', 'Solid'),      color: 'blue'   } :
    result.score >= 40 ? { label: t('dashboard.skillAssessment.bandDeveloping', 'Developing'), color: 'amber'  } :
                         { label: t('dashboard.skillAssessment.bandFoundations', 'Foundations'), color: 'red'    };
  const ringColor = {
    green: '#22c55e',
    blue:  '#3b82f6',
    amber: '#f59e0b',
    red:   '#ef4444',
  }[scoreBand.color];

  return (
    <section className="space-y-5">
      {/* Recalibration toast */}
      {(recalibrating || recalibrated) && (
        <div className={`rounded-xl border px-4 py-3 text-sm flex items-center gap-2 transition-colors
          ${recalibrated
            ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400'
            : 'vscode-border vscode-bg-tertiary vscode-text-secondary'}
        `}>
          {recalibrating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4" />
          )}
          <span>
            {recalibrating
              ? t('dashboard.skillAssessment.recalibrating', 'Updating your job recommendations…')
              : t('dashboard.skillAssessment.recalibrated', 'Recommendations recalibrated based on your new score.')}
          </span>
        </div>
      )}

      {/* Score header */}
      <div className="rounded-xl border vscode-border vscode-bg-secondary p-5 flex items-center gap-4 sm:gap-6">
        <ScoreRing score={result.score} color={ringColor} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Trophy className="w-5 h-5 text-[var(--vscode-primary)]" />
            <h2 className="text-xl font-semibold vscode-text-primary">
              {t('dashboard.skillAssessment.scoreOnSkill', {
                defaultValue: '{{band}} on {{skill}}',
                band: scoreBand.label,
                skill,
              })}
            </h2>
          </div>
          <p className="text-sm vscode-text-secondary mt-1">
            {t('dashboard.skillAssessment.correctAtLevel', {
              defaultValue: '{{correct}} / {{total}} correct at the {{level}} level.',
              correct: result.correct,
              total: result.total,
              level,
            })}
          </p>
        </div>
      </div>

      {/* AI feedback */}
      {result.feedback && (
        <div className="rounded-xl border vscode-border vscode-bg-tertiary p-4 sm:p-5">
          <p className="text-xs uppercase tracking-wider font-semibold vscode-text-tertiary mb-2">
            {t('dashboard.skillAssessment.focusNext', 'What to focus on next')}
          </p>
          <p className="text-sm vscode-text-secondary leading-relaxed">{result.feedback}</p>
        </div>
      )}

      {/* Per-question breakdown */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider font-semibold vscode-text-tertiary">
          {t('dashboard.skillAssessment.breakdown', 'Question breakdown')}
        </p>
        {result.graded.map((q, i) => {
          const isOpen = expanded[i] === true;
          const StatusIcon = q.is_correct ? CheckCircle2 : XCircle;
          const statusColor = q.is_correct
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';
          return (
            <div
              key={i}
              className={`rounded-xl border vscode-border vscode-bg-secondary overflow-hidden transition-all
                ${q.is_correct ? '' : 'border-red-500/30'}
              `}
            >
              <button
                type="button"
                onClick={() => toggle(i)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:vscode-bg-hover transition-colors"
              >
                <StatusIcon className={`w-5 h-5 shrink-0 mt-0.5 ${statusColor}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium vscode-text-primary leading-snug">
                    {i + 1}. {q.q}
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 vscode-text-tertiary shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t vscode-border">
                  <ul className="space-y-1.5">
                    {q.options.map((opt, idx) => {
                      const isCorrect = idx === q.correct_index;
                      const isSelected = idx === q.selected_index;
                      return (
                        <li
                          key={idx}
                          className={`text-sm px-3 py-2 rounded-lg border flex items-start gap-2
                            ${isCorrect
                              ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400'
                              : isSelected
                                ? 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400'
                                : 'vscode-border vscode-text-secondary'}
                          `}
                        >
                          <span className="font-bold shrink-0">
                            {String.fromCharCode(65 + idx)}.
                          </span>
                          <span className="flex-1">{opt}</span>
                          {isCorrect && <Check className="w-4 h-4 shrink-0" />}
                          {isSelected && !isCorrect && <X className="w-4 h-4 shrink-0" />}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="text-xs vscode-text-tertiary leading-relaxed">
                    <span className="font-semibold vscode-text-secondary">{t('dashboard.skillAssessment.why', 'Why:')}</span>{' '}
                    {q.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <button
          type="button"
          onClick={onRetry}
          className="min-h-[44px] px-5 rounded-xl bg-[var(--vscode-primary)] text-white text-sm font-semibold hover:opacity-90 inline-flex items-center justify-center gap-2 touch-manipulation"
        >
          <RefreshCw className="w-4 h-4" /> {t('dashboard.skillAssessment.tryAnotherSkill', 'Try another skill')}
        </button>
      </div>
    </section>
  );
};

// ── Score ring (SVG, 80×80) ──────────────────────────────────────────────────

const ScoreRing: React.FC<{ score: number; color: string }> = ({ score, color }) => {
  const pct = Math.max(0, Math.min(100, score));
  const r = 30;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" className="shrink-0" aria-hidden="true">
      <circle cx="40" cy="40" r={r} fill="none" stroke="currentColor" strokeWidth="5" className="text-[var(--vscode-text-tertiary)]/15" />
      <circle
        cx="40" cy="40" r={r} fill="none"
        stroke={color}
        strokeWidth="5"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 40 40)"
      />
      <text x="40" y="46" textAnchor="middle" fontSize="20" fontWeight="700" fill={color}>
        {pct}
      </text>
    </svg>
  );
};

export default SkillAssessment;
